'use client'

// src/beacon.tsx
import {
  chakra,
  forwardRef,
  useStyleConfig
} from "@chakra-ui/react";
import { cx } from "@chakra-ui/utils";
import { jsx } from "react/jsx-runtime";
var Beacon = forwardRef((props, ref) => {
  const styles = useStyleConfig("SuiBeacon", props);
  return /* @__PURE__ */ jsx(
    chakra.div,
    {
      ref,
      ...props,
      __css: styles,
      className: cx("sui-beacon", props.className)
    }
  );
});
Beacon.displayName = "Beacon";

// src/benefits-modal.tsx
import {
  Image,
  Modal,
  ModalContent,
  ModalCloseButton as BenefitsModalCloseButton,
  ModalBody as BenefitsModalBody,
  ModalHeader as BenefitsModalHeader,
  ModalFooter as BenefitsModalFooter,
  ModalOverlay,
  AspectRatio
} from "@chakra-ui/react";

// src/use-tour-dialog.ts
import * as React from "react";
import {
  mergeRefs,
  useDisclosure
} from "@chakra-ui/react";
import { createContext } from "@chakra-ui/utils";
var [TourDialogContextProvider, useTourDialogContext] = createContext();
var useTourDialog = (props) => {
  const {
    initialFocusRef,
    onSubmit,
    isOpen: isOpenProp,
    onClose: onCloseProp,
    defaultIsOpen,
    primaryAction,
    secondaryAction
  } = props;
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure({
    defaultIsOpen,
    isOpen: isOpenProp,
    onClose: onCloseProp
  });
  const primaryActionRef = React.useRef(null);
  const getPrimaryActionProps = React.useCallback(
    (props2, ref) => {
      return {
        label: "OK",
        ...primaryAction,
        ...props2,
        ref: mergeRefs(primaryActionRef, ref)
      };
    },
    [onSubmit, onClose, primaryActionRef]
  );
  const getSecondaryActionProps = React.useCallback(
    (props2) => {
      return {
        label: "Dismiss",
        ...secondaryAction,
        ...props2
      };
    },
    [onClose]
  );
  return {
    initialFocusRef: initialFocusRef || primaryActionRef,
    isOpen,
    onOpen,
    onClose,
    onToggle,
    getPrimaryActionProps,
    getSecondaryActionProps
  };
};

// src/tour-dialog.tsx
import * as React3 from "react";
import {
  Button,
  ButtonGroup,
  Popover,
  PopoverAnchor as TourDialogAnchor,
  PopoverArrow as TourDialogArrow,
  PopoverBody as TourDialogBody,
  PopoverCloseButton as TourDialogCloseButton,
  PopoverContent as TourDialogContent,
  PopoverFooter as TourDialogFooter,
  PopoverHeader as TourDialogHeader,
  PopoverTrigger as TourDialogTrigger,
  forwardRef as forwardRef2,
  usePopoverContext,
  useTheme
} from "@chakra-ui/react";
import { getChildOfType } from "@saas-ui/react-utils";

// src/tour-dialog.styles.ts
import { createMultiStyleConfigHelpers } from "@chakra-ui/styled-system";
import { cssVar, mode } from "@chakra-ui/theme-tools";
var parts = [
  "popper",
  "content",
  "header",
  "body",
  "footer",
  "arrow",
  "closeButton"
];
var { defineMultiStyleConfig, definePartsStyle } = createMultiStyleConfigHelpers(parts);
var $popperBg = cssVar("tour-dialog-bg");
var $arrowBg = cssVar("popper-arrow-bg");
var $arrowShadowColor = cssVar("popper-arrow-shadow-color");
var baseStylePopper = {
  zIndex: "modal"
};
var baseStyleContent = (props) => {
  const { colorScheme: c } = props;
  const bg = `${c}.500`;
  const shadowColor = mode("blackAlpha.200", "whiteAlpha.300")(props);
  return {
    [$popperBg.variable]: `colors.${bg}`,
    bg: $popperBg.reference,
    [$arrowBg.variable]: $popperBg.reference,
    [$arrowShadowColor.variable]: `colors.${shadowColor}`,
    color: "white",
    width: "xs",
    border: "1px solid",
    borderColor: "inherit",
    borderRadius: "md",
    boxShadow: "sm",
    zIndex: "inherit",
    _focus: {
      outline: 0
    }
  };
};
var baseStyleHeader = {
  px: 4,
  py: 3,
  fontWeight: "bold"
};
var baseStyleBody = {
  px: 3,
  py: 2,
  fontSize: "md"
};
var baseStyleFooter = {
  px: 3,
  py: 2,
  display: "flex",
  alignItems: "center"
};
var baseStyleCloseButton = {
  position: "absolute",
  borderRadius: "md",
  top: 1,
  insetEnd: 2,
  padding: 2
};
var baseStyle = definePartsStyle((props) => ({
  popper: baseStylePopper,
  content: baseStyleContent(props),
  header: baseStyleHeader,
  body: baseStyleBody,
  footer: baseStyleFooter,
  arrow: {},
  closeButton: baseStyleCloseButton
}));
var tour_dialog_styles_default = defineMultiStyleConfig({
  defaultProps: {
    colorScheme: "primary"
  },
  baseStyle
});

// src/use-tour.tsx
import * as React2 from "react";
import { useControllableState } from "@chakra-ui/react";
import { callAllHandlers, createContext as createContext2 } from "@chakra-ui/utils";
import { useSteps } from "@saas-ui/react";
var useTour = (props) => {
  const {
    steps,
    initialStep,
    isActive: isActiveProp,
    defaultIsActive,
    onComplete,
    onDismiss
  } = props;
  const [isActive, setActive] = useControllableState({
    defaultValue: defaultIsActive,
    value: isActiveProp
  });
  const {
    index,
    step,
    isLast,
    reset,
    setComplete,
    setUncomplete,
    completed,
    navigation
  } = useSteps({
    steps,
    initialStep
  });
  const [targetElement, setTarget] = React2.useState(null);
  const targetRef = React2.useRef(null);
  React2.useEffect(() => {
    if (!step?.id) {
      return;
    }
    const el = document.querySelector(step.id);
    targetRef.current = el;
    setTarget(el);
  }, [steps, step]);
  const start = React2.useCallback(() => {
    setActive(true);
  }, []);
  const stop = React2.useCallback(() => {
    setActive(false);
  }, []);
  const next = React2.useCallback(() => {
    setComplete(index);
    if (isLast) {
      stop();
      onComplete?.();
    } else {
      navigation.next();
    }
  }, [setComplete, index, navigation, isLast]);
  const prev = React2.useCallback(() => {
    setUncomplete(index);
    navigation.prev();
  }, [setUncomplete, index, navigation]);
  const dismiss = React2.useCallback(() => {
    setActive(false);
    onDismiss?.(index);
  }, [onDismiss, index]);
  const getStepProps = React2.useCallback(() => {
    return {
      isOpen: isActive,
      onClose: dismiss
    };
  }, [isActive, dismiss]);
  const getNextProps = React2.useCallback(
    (props2) => {
      return {
        ...props2,
        onClick: callAllHandlers(props2?.onClick, next)
      };
    },
    [next]
  );
  const getPrevProps = React2.useCallback(
    (props2) => {
      return {
        ...props2,
        onClick: callAllHandlers(props2?.onClick, prev)
      };
    },
    [prev]
  );
  const getDismissProps = React2.useCallback(
    (props2) => {
      return {
        ...props2,
        onClick: callAllHandlers(props2?.onClick, dismiss)
      };
    },
    [onDismiss, dismiss]
  );
  return {
    isActive,
    isCompleted: completed.length === steps.length,
    targetElement,
    targetRef,
    index,
    step,
    start,
    stop,
    next,
    prev,
    reset,
    getStepProps,
    getNextProps,
    getPrevProps,
    getDismissProps
  };
};
var [TourProvider, useTourContext] = createContext2();

// src/tour-dialog.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var TourDialog = forwardRef2((props, ref) => {
  const { children, hideArrow } = props;
  const triggerComponent = getChildOfType(children, TourDialogTrigger);
  const _children = React3.Children.toArray(children).filter((child) => {
    return child.type !== TourDialogTrigger;
  });
  const target = props["data-target"];
  return /* @__PURE__ */ jsxs(TourDialogContainer, { ...props, children: [
    triggerComponent,
    target && /* @__PURE__ */ jsx2(TourDialogTarget, {}),
    /* @__PURE__ */ jsxs(TourDialogContent, { ref, children: [
      !hideArrow && /* @__PURE__ */ jsx2(TourDialogArrow, {}),
      _children
    ] })
  ] });
});
TourDialog.displayName = "TourDialog";
var TourDialogContainer = (props) => {
  const { children, ...rest } = props;
  const context = useTourDialog(props);
  const { initialFocusRef, isOpen, onClose } = context;
  const theme = useTheme();
  const styleConfig = theme.components?.TourDialog || tour_dialog_styles_default;
  return /* @__PURE__ */ jsx2(TourDialogContextProvider, { value: context, children: /* @__PURE__ */ jsx2(
    Popover,
    {
      initialFocusRef,
      styleConfig,
      isOpen,
      onClose,
      closeOnBlur: false,
      ...rest,
      children
    }
  ) });
};
TourDialogContainer.displayName = "TourDialogContainer";
var TourDialogActions = (props) => {
  return /* @__PURE__ */ jsx2(
    ButtonGroup,
    {
      size: "sm",
      variant: "ghost",
      flex: "1",
      justifyContent: "flex-end",
      ...props
    }
  );
};
TourDialogActions.displayName = "TourDialogActions";
var TourDialogPrimaryAction = (props) => {
  const { getPrimaryActionProps } = useTourDialogContext();
  return /* @__PURE__ */ jsx2(Button, { ...getPrimaryActionProps({ variant: "subtle", ...props }) });
};
TourDialogPrimaryAction.displayName = "TourDialogPrimaryAction";
var TourDialogSecondaryAction = (props) => {
  const { getSecondaryActionProps } = useTourDialogContext();
  return /* @__PURE__ */ jsx2(Button, { ...getSecondaryActionProps(props) });
};
TourDialogSecondaryAction.displayName = "TourDialogSecondaryAction";
var TourDialogTarget = () => {
  const { getAnchorProps } = usePopoverContext();
  const context = useTourContext();
  if (!context) {
    return null;
  }
  const { targetElement } = context;
  const { ref } = getAnchorProps();
  React3.useEffect(() => {
    if (typeof ref === "function") {
      ref?.(targetElement);
    }
  }, [targetElement]);
  return null;
};

// src/benefits-modal.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var BenefitsModal = (props) => {
  const { children, hideOverlay, hideCloseButton, ...containerProps } = props;
  return /* @__PURE__ */ jsxs2(BenefitsModalContainer, { ...containerProps, children: [
    !hideOverlay && /* @__PURE__ */ jsx3(ModalOverlay, {}),
    /* @__PURE__ */ jsxs2(ModalContent, { children: [
      !hideCloseButton && /* @__PURE__ */ jsx3(BenefitsModalCloseButton, {}),
      children
    ] })
  ] });
};
var BenefitsModalContainer = (props) => {
  const { children, isCentered = true, ...rest } = props;
  const context = useTourDialog(props);
  const { initialFocusRef, isOpen, onClose } = context;
  return /* @__PURE__ */ jsx3(TourDialogContextProvider, { value: context, children: /* @__PURE__ */ jsx3(
    Modal,
    {
      initialFocusRef,
      ...rest,
      isCentered,
      isOpen,
      onClose,
      children
    }
  ) });
};
BenefitsModalContainer.displayName = "BenefitsModalContainer";
var BenefitsModalMedia = (props) => {
  const { children, src, ...rest } = props;
  const content = src ? /* @__PURE__ */ jsx3(Image, { src }) : children;
  return /* @__PURE__ */ jsx3(AspectRatio, { ratio: 1, ...rest, children: content });
};
BenefitsModalMedia.displayName = "BenefitsModalMedia";
var BenefitsModalActions = (props) => {
  return /* @__PURE__ */ jsx3(TourDialogActions, { ...props });
};
BenefitsModalActions.displayName = "BenefitsModalActions";

// src/tour.tsx
import * as React4 from "react";
import { Button as Button2, Portal } from "@chakra-ui/react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var Tour = (props) => {
  const { children, tourRef } = props;
  const _steps = React4.Children.toArray(children).filter((child) => {
    return React4.isValidElement(child) && child.props["data-target"];
  });
  const _children = React4.Children.toArray(children).filter((child) => {
    return React4.isValidElement(child) && !child.props["data-target"];
  });
  const steps = React4.Children.map(_steps, (child) => {
    return {
      id: child.props["data-target"],
      element: child
    };
  });
  const ctx = useTour({ steps, ...props });
  React4.useImperativeHandle(tourRef, () => ctx, [ctx]);
  const { step, getStepProps } = ctx;
  const currentStep = step?.element && React4.cloneElement(step?.element, getStepProps());
  return /* @__PURE__ */ jsxs3(TourProvider, { value: ctx, children: [
    /* @__PURE__ */ jsx4(Portal, { children: currentStep }),
    _children
  ] });
};
var TourNextButton = (props) => {
  const { getNextProps } = useTourContext();
  return /* @__PURE__ */ jsx4(
    Button2,
    {
      variant: "solid",
      colorScheme: "primary",
      children: "Next",
      ...getNextProps(props)
    }
  );
};
TourNextButton.displayName = "TourNextButton";
var TourPrevButton = (props) => {
  const { getPrevProps } = useTourContext();
  return /* @__PURE__ */ jsx4(Button2, { children: "Back", ...getPrevProps(props) });
};
TourPrevButton.displayName = "TourPrevButton";
var TourDismissButton = (props) => {
  const { getDismissProps } = useTourContext();
  return /* @__PURE__ */ jsx4(Button2, { children: "Dismiss", ...getDismissProps(props) });
};
TourDismissButton.displayName = "TourDismissButton";

// src/tour-spotlight.tsx
import {
  chakra as chakra2,
  fadeConfig,
  Portal as Portal2,
  useStyleConfig as useStyleConfig2
} from "@chakra-ui/react";
import { cx as cx2 } from "@chakra-ui/utils";
import { motion } from "framer-motion";
import * as React6 from "react";

// src/use-tour-spotlight.ts
import * as React5 from "react";
import { useSafeLayoutEffect } from "@chakra-ui/react";
import { callAllHandlers as callAllHandlers2 } from "@chakra-ui/utils";
function useTourSpotlight() {
  const [dimensions, setDimensions] = React5.useState(null);
  const { isActive, stop, targetElement } = useTourContext();
  const rafId = React5.useRef();
  useSafeLayoutEffect(() => {
    if (!isActive) return void 0;
    function measure() {
      rafId.current = requestAnimationFrame(() => {
        const rect = targetElement?.getBoundingClientRect() || null;
        setDimensions(rect);
      });
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [targetElement, isActive]);
  const getSpotlightProps = React5.useCallback(
    (props = {}) => {
      const {
        spacing = 4,
        closeOnClick = false,
        hideOverlay,
        onClick,
        sx
      } = props;
      if (isActive) {
        const scrollTop = document.scrollingElement?.scrollTop || document.documentElement.scrollTop || 0;
        const scrollLeft = document.scrollingElement?.scrollLeft || document.documentElement.scrollLeft || 0;
        return {
          animate: "enter",
          sx: {
            width: dimensions ? dimensions.width + spacing * 2 + "px" : "0",
            height: dimensions ? dimensions.height + spacing * 2 + "px" : "0",
            top: dimensions ? dimensions.top + scrollTop - spacing + "px" : "-10px",
            left: dimensions ? dimensions.left + scrollLeft - spacing + "px" : "50%",
            boxShadow: !hideOverlay ? "0 0 0 9999px rgba(0, 0, 0, 0.2)" : "none",
            pointerEvents: closeOnClick ? "auto" : "none",
            ...sx
          },
          onClick: callAllHandlers2(onClick, () => {
          })
        };
      }
      return {
        animate: "exit",
        sx
      };
    },
    [isActive, dimensions, stop]
  );
  return {
    isActive,
    getSpotlightProps
  };
}

// src/tour-spotlight.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var MotionBox = chakra2(motion.div);
var TourSpotlight = React6.forwardRef(
  (props, ref) => {
    const { motionPreset, ...rest } = props;
    const styles = useStyleConfig2("SuiSpotlight", props);
    const { getSpotlightProps } = useTourSpotlight();
    const spotlightStyles = {
      position: "absolute",
      zIndex: "overlay",
      transitionProperty: "all",
      transitionDuration: "slow",
      borderRadius: "md",
      borderWidth: "2px",
      borderColor: "primary.500",
      ...styles
    };
    const motionProps = motionPreset === "none" ? {} : fadeConfig;
    return /* @__PURE__ */ jsx5(Portal2, { children: /* @__PURE__ */ jsx5(
      MotionBox,
      {
        ...rest,
        ...motionProps,
        ...getSpotlightProps(props),
        ref,
        __css: spotlightStyles,
        className: cx2("sui-spotlight", props.className)
      }
    ) });
  }
);
TourSpotlight.displayName = "TourSpotlight";

// src/use-tour-beacon.ts
import * as React7 from "react";
import {
  popperCSSVars,
  useDisclosure as useDisclosure2,
  usePopper
} from "@chakra-ui/react";
function useTourBeacon(props = {}) {
  const {
    onOpen: onOpenProp,
    onClose: onCloseProp,
    placement = "right-start",
    id,
    isOpen: isOpenProp,
    defaultIsOpen,
    modifiers,
    isDisabled,
    gutter,
    offset = [-4, -4],
    direction,
    ...htmlProps
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure2({
    isOpen: isOpenProp,
    defaultIsOpen,
    onOpen: onOpenProp,
    onClose: onCloseProp
  });
  const { referenceRef, getPopperProps } = usePopper({
    enabled: isOpen,
    placement,
    modifiers,
    gutter,
    offset,
    direction
  });
  const { start } = useTourContext();
  const tooltipId = id ?? React7.useId();
  const { targetElement, isActive, isCompleted } = useTourContext();
  React7.useEffect(() => {
    if (typeof referenceRef === "function") {
      referenceRef?.(targetElement);
    }
  }, [targetElement]);
  React7.useEffect(() => {
    targetElement && !isActive && !isCompleted ? onOpen() : onClose();
  }, [targetElement, isActive]);
  const onClick = React7.useCallback(() => {
    if (isDisabled) {
      return;
    }
    start();
  }, [start, isDisabled]);
  const getTourBeaconPositionerProps = React7.useCallback(
    (props2 = {}, forwardedRef = null) => getPopperProps(
      {
        ...props2,
        style: {
          ...props2.style
        }
      },
      forwardedRef
    ),
    [getPopperProps]
  );
  const getTourBeaconProps = React7.useCallback(
    (props2 = {}, ref = null) => {
      const tooltipProps = {
        ref,
        ...htmlProps,
        ...props2,
        id: tooltipId,
        role: "tooltip",
        onClick,
        style: {
          ...props2.style,
          position: "relative",
          transformOrigin: popperCSSVars.transformOrigin.varRef
        }
      };
      return tooltipProps;
    },
    [htmlProps, tooltipId, onClick]
  );
  return {
    isOpen,
    getTourBeaconProps,
    getTourBeaconPositionerProps
  };
}
export {
  Beacon,
  BenefitsModal,
  BenefitsModalActions,
  BenefitsModalBody,
  BenefitsModalCloseButton,
  BenefitsModalContainer,
  BenefitsModalFooter,
  BenefitsModalHeader,
  BenefitsModalMedia,
  Tour,
  TourDialog,
  TourDialogActions,
  TourDialogAnchor,
  TourDialogArrow,
  TourDialogBody,
  TourDialogCloseButton,
  TourDialogContainer,
  TourDialogContextProvider,
  TourDialogFooter,
  TourDialogHeader,
  TourDialogPrimaryAction,
  TourDialogSecondaryAction,
  TourDialogTarget,
  TourDialogTrigger,
  TourDismissButton,
  TourNextButton,
  TourPrevButton,
  TourProvider,
  TourSpotlight,
  useTour,
  useTourBeacon,
  useTourContext,
  useTourDialog,
  useTourDialogContext,
  useTourSpotlight
};
//# sourceMappingURL=index.mjs.map